import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CartItem } from '@queries/generated';
import { EcommerceTrackingService } from '@services/ecommerce-tracking.service';
import { ShoppingCartService } from '@services/shopping-cart.service';
import { MessageService } from 'primeng/api';
import { InputNumber } from 'primeng/inputnumber';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'vnm-cart-item',
  templateUrl: './cart-item.component.html',
  styleUrls: ['./cart-item.component.scss']
})
export class CartItemComponent implements OnInit, OnChanges {
  @ViewChild('cartItemTemplate', { static: true }) cartItemTemplate;
  @ViewChild('quantityInput', { static: false }) quantityInput: InputNumber;

  @Input() cartItem!: CartItem;

  quantity$ = new Subject<number>();
  isDiscounted = false;
  hasBulkDiscounts = false;
  minQuantityAvailableForBooking = 0;

  constructor(
    private shoppingCartService: ShoppingCartService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private ecom: EcommerceTrackingService
  ) {}

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.cartItemTemplate);
    this.quantity$
      .pipe(
        untilDestroyed(this),
        debounceTime(500),
        map((value) => Math.max(value, 1))
      )
      .subscribe(this.updateCartItem);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.cartItem) {
      const cartItem = changes.cartItem.currentValue;

      const { bulkDiscountApplied, promoCodeClaim, product } = cartItem;
      this.isDiscounted = bulkDiscountApplied || promoCodeClaim !== null;
      this.hasBulkDiscounts = product.bulkDiscounts;
      this.minQuantityAvailableForBooking = product.bulkDiscountOptions?.levels[0]?.min ?? 0;
    }
  }

  onQuantityChange(quantity: number) {
    this.quantity$.next(quantity);
  }

  onRemovedFromCart(cartItem: CartItem) {
    this.ecom.removeFromCart(cartItem);
  }

  private updateCartItem = (quantity: number) => {
    this.shoppingCartService
      .updateCartItem({
        cartItem: this.cartItem.gid,
        patch: { quantity }
      })
      .subscribe(
        () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('cart-item.cart-item-updated')
          });
        },
        (errors) => {
          errors.forEach((error) => {
            this.messageService.add({
              severity: 'error',
              summary: error.message
            });
          });

          this.quantityInput.writeValue(this.cartItem.quantity); // bindings don't update the input for some reason
        }
      );
  };
}
